// "Your practice" - this team's published practice slot, above the
// preferences form. Seven states are deliberately distinct: a team that gets no
// practice at all (excluded - 4U/5U or dormant; it outranks every other state
// and is the only one that must NOT promise an email), no published run
// yet (not_scheduled), a published run in which this team has no slot at all
// (unplaced), a slot that exists but has not started yet (upcoming_only, only
// `upcoming` is populated), a team holding every practice it is supposed to
// (assigned, `current` is populated), and an Upper Division team holding only
// ONE of its two (partially_placed), and a team whose practice end has
// passed (ended). None of the copy may read like another
// state: a coach whose team was not placed needs to know that, not to keep
// waiting like not_scheduled, a coach whose slot starts later needs the start
// date, not the unplaced message repeated, and a UD coach missing their
// second practice must not be told they are done (that is what `assigned`
// would silently claim if `current` stayed singular).

import { useQuery } from '@tanstack/react-query'
import type { CoachPracticeSlot, CoachPracticeView } from '@region13/shared-types'
import { api } from '../lib/api'
import { shortDate } from '../lib/practiceDate'
import { practiceMovingLine, SECOND_PRACTICE_PENDING } from '../lib/practiceCopy'

const boxClass = 'rounded-md bg-canvas px-3 py-2 text-sm'
const mutedBox = `${boxClass} text-ink-soft`

function SlotLines({ slot }: { slot: CoachPracticeSlot }) {
  return (
    <>
      <p className="font-semibold text-ink">{slot.when}</p>
      <p className="text-ink">{slot.where}</p>
      <a className="text-sm font-semibold text-brand-red-dark underline" href={slot.mapUrl} target="_blank" rel="noopener">
        View field map
      </a>
    </>
  )
}

function NotScheduled() {
  return <div className={mutedBox}>Practice fields have not been assigned yet. You will get an email when they are.</div>
}

function Unplaced() {
  return (
    <div className={mutedBox}>
      This team does not have a practice slot yet. We are still working on a field for you, and we will email you when there is
      one.
    </div>
  )
}

/** 4U/5U (and a dormant team) get no practice at all, so this must not promise
 *  an email like `NotScheduled` and `Unplaced` both do. It says what happens
 *  instead, because "no practice field" without that would read as something
 *  having gone wrong. */
function Excluded() {
  return (
    <div className={mutedBox}>
      This team does not have a separate practice field. Your group meets at the same field and time each week, arranged by the
      preschool coordinator.
    </div>
  )
}

/** The team's practice end has passed. Says so plainly and promises nothing:
 *  it is not `Unplaced`, which tells the coach a slot is coming. */
function Ended({ through }: { through: string }) {
  return (
    <div className={boxClass}>
      <p className="font-semibold text-ink">Practice ended {shortDate(through)}.</p>
      <p className="mt-1 text-sm text-ink-soft">This season's practice schedule is over for your team.</p>
    </div>
  )
}

/** A slot exists but its `effectiveFromDate` has not arrived: lead with the
 *  start date so this reads as "placed, starting later," not "not placed."
 *  Plural because an Upper Division team's two practices can both be
 *  not-yet-effective at once (see `selectTeamSlots`). */
function UpcomingOnly({ slots }: { slots: CoachPracticeSlot[] }) {
  return (
    <div className={boxClass}>
      {slots.map((slot, i) => (
        <div key={`${slot.when}-${slot.where}`} className={i > 0 ? 'mt-3' : undefined}>
          <p className="font-semibold text-ink">
            Your practice starts on {slot.effectiveFromDate ? shortDate(slot.effectiveFromDate) : 'a later date'}.
          </p>
          <SlotLines slot={slot} />
        </div>
      ))}
    </div>
  )
}

/** The re-home line(s), one per later-dated slot. Same sentence as Home's
 *  summary row, from one tested function — the two surfaces drifted apart
 *  before, and only this one got the fix. */
function UpcomingLines({ upcoming }: { upcoming: CoachPracticeSlot[] }) {
  return (
    <>
      {upcoming.map((slot) => (
        <p key={`${slot.when}-${slot.where}`} className="mt-2 text-ink-soft">
          {practiceMovingLine(slot)}
        </p>
      ))}
    </>
  )
}

function Assigned({ current, upcoming, through }: { current: CoachPracticeSlot[]; upcoming: CoachPracticeSlot[]; through: string | null }) {
  return (
    <div className={boxClass}>
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Your practice</p>
      {current.map((slot, i) => (
        <div key={`${slot.when}-${slot.where}`} className={i > 0 ? 'mt-3' : undefined}>
          <SlotLines slot={slot} />
        </div>
      ))}
      {through ? <p className="mt-2 text-sm text-ink-soft">Through {shortDate(through)}</p> : null}
      <UpcomingLines upcoming={upcoming} />
    </div>
  )
}

/** A twice-a-week team holding ONE of its two practices. Same list as
 *  `Assigned` plus the second-practice sentence.
 *
 *  Reachable by any team whose `expectedCount` is 2, which since migration
 *  0073 is no longer only Upper Division: an "Extra" team (B10-X) is 10U and
 *  practices twice. What still cannot reach this branch is a team expecting
 *  ONE practice, because `current.length` is then 0 or 1 and neither is a
 *  partial (see `coachPracticeView`'s status derivation). */
function PartiallyPlaced({ current, upcoming }: { current: CoachPracticeSlot[]; upcoming: CoachPracticeSlot[] }) {
  return (
    <div className={boxClass}>
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Your practice</p>
      {current.map((slot, i) => (
        <div key={`${slot.when}-${slot.where}`} className={i > 0 ? 'mt-3' : undefined}>
          <SlotLines slot={slot} />
        </div>
      ))}
      <p className="mt-2 text-ink-soft">{SECOND_PRACTICE_PENDING}</p>
      <UpcomingLines upcoming={upcoming} />
    </div>
  )
}

/** Defensive fallback for a shape the API contract rules out (an
 *  upcoming_only/assigned/partially_placed status whose matching slot list is
 *  empty) so a data glitch surfaces honestly instead of showing the unplaced
 *  promise. */
function SlotDataError() {
  return (
    <div className={`${boxClass} text-brand-red-dark`} role="alert">
      Couldn't load your practice slot. Please try again.
    </div>
  )
}

export function PracticeSlotBody({ view }: { view: CoachPracticeView }) {
  // 'excluded' first: it is not a stage in the process, it is "never applies".
  if (view.status === 'excluded') return <Excluded />
  if (view.status === 'not_scheduled') return <NotScheduled />
  if (view.status === 'unplaced') return <Unplaced />
  if (view.status === 'ended') return view.through ? <Ended through={view.through} /> : <SlotDataError />
  if (view.status === 'upcoming_only') return view.upcoming.length > 0 ? <UpcomingOnly slots={view.upcoming} /> : <SlotDataError />
  if (view.status === 'assigned') return view.current.length > 0 ? <Assigned current={view.current} upcoming={view.upcoming} through={view.through} /> : <SlotDataError />
  if (view.status === 'partially_placed') {
    return view.current.length > 0 ? <PartiallyPlaced current={view.current} upcoming={view.upcoming} /> : <SlotDataError />
  }
  return null
}

export function PracticeSlotCard({ teamExternalId }: { teamExternalId: string }) {
  const q = useQuery({
    queryKey: ['practice-slot', teamExternalId],
    queryFn: () => api.practiceSlot(teamExternalId),
  })

  if (q.isLoading) {
    return (
      <div className={mutedBox} role="status">
        Loading your practice slot…
      </div>
    )
  }
  if (q.isError || !q.data) {
    return <SlotDataError />
  }
  return <PracticeSlotBody view={q.data} />
}
